import { Locale } from '@/domain/models/locale'

export const elapseDaysBetweenMilliseconds = (
  firstMillisecond: number,
  secondMillisecond: number
) => {
  const elapsedDays = Math.abs(firstMillisecond - secondMillisecond)
  const dayInMilliSeconds = 1000 * 60 * 60 * 24

  return Math.floor(elapsedDays / dayInMilliSeconds)
}

export const formatDateString = (
  dateString: string | undefined,
  locale: Locale.Enum = Locale.Enum.BR
) => dateString && new Date(dateString).toLocaleDateString(locale.value)

export const formatDateUTCString = (
  dateString: string | undefined,
  locale: Locale.Enum = Locale.Enum.BR
) => {
  if (!dateString) return

  const [year, month, day] = dateString.split('-').map(Number)
  const date = new Date(year, month - 1, day)

  return date.toLocaleDateString(locale.value)
}

export const formatDate = (date: Date | null | undefined, locale: Locale.Enum = Locale.Enum.BR) =>
  date?.toLocaleDateString(locale.value)

export const formatIsoLikeDate = (date: Date | null | undefined) => {
  if (!date) return
  const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
  return utcDate.toISOString().replace(/T.*/g, '')
}

export const createLocalTimezoneDateFromString = (dateString: string | null | undefined) => {
  if (!dateString) return null
  return new Date(`${dateString.replace(/T.*/g, '')}T00:00`)
}

export const formatDateSmall = (
  date: Date | null | undefined,
  locale: Locale.Enum = Locale.Enum.BR
) => date?.toLocaleDateString(locale.value, { month: '2-digit', day: '2-digit' })

export const formatTime = (date?: Date) =>
  date?.toLocaleTimeString(Locale.Enum.BR.value, { hour: '2-digit', minute: '2-digit' })

export const formatCustomDateString = (dateString: string, format: string): string | undefined => {
  const [day, month, year] = dateString.split('/').map(part => parseInt(part, 10))

  const date = new Date(year, month - 1, day)

  if (isNaN(date.getTime())) {
    return
  }

  const map: { [key: string]: string } = {
    mm: ('0' + (date.getMonth() + 1)).slice(-2),
    dd: ('0' + date.getDate()).slice(-2),
    yyyy: date.getFullYear().toString(),
    yy: date.getFullYear().toString().slice(-2)
  }

  return format.replace(/mm|dd|yyyy|yy/gi, matched => map[matched])
}

export const getAge = (birthdate: string | null | undefined) => {
  if (!birthdate) {
    return null
  }
  try {
    const dateParts = birthdate.split('/').map(v => parseInt(v))
    const diff = Date.now() - new Date(dateParts[2], dateParts[1], dateParts[0]).getTime()
    return Math.abs(new Date(diff).getUTCFullYear() - 1970)
  } catch (e) {
    return null
  }
}

export function formatMinutesForHours(minutes: number): { hours: number; minute: number } {
  const hours = Math.floor(minutes / 60)
  const minute = minutes % 60

  return { hours, minute }
}

export function formatTimeString(minutes: number): string {
  const { hours, minute } = formatMinutesForHours(minutes)
  if (hours && minute) return `${hours}h${minute}min`
  if (hours) return `${hours}h`
  return `${minute}min`
}

export function formatLocaleStartTime(
  startTime: string,
  t: { components: (key: string, params?: Record<string, string>) => string }
): string {
  try {
    const dateTime = new Date(`${new Date().toISOString().split('T')[0]}T${startTime}`)
    const formatter = new Intl.DateTimeFormat(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    })
    const localeStartTime = formatter.format(dateTime)
    const isAMPM = localeStartTime.includes('AM') || localeStartTime.includes('PM')

    const timeMatch = localeStartTime.match(/(\d{1,2})[:h](\d{2})/)
    const formattedStartTime = timeMatch ? `${timeMatch[1]}h${timeMatch[2]}` : ''

    if (isAMPM) {
      const isAM = localeStartTime.includes('AM')
      return isAM
        ? t.components('contentUnitAttributes.localeStartTime.titleAM', {
            startTime: formattedStartTime
          })
        : t.components('contentUnitAttributes.localeStartTime.titlePM', {
            startTime: formattedStartTime
          })
    } else {
      return t.components('contentUnitAttributes.localeStartTime.titleAM', {
        startTime: formattedStartTime
      })
    }
  } catch (error) {
    return t.components('contentUnitAttributes.localeStartTime.titleAM', {
      startTime: startTime
    })
  }
}

export function calculateDaysRemaining(enrollmentDeadline: string | undefined): number {
  if (!enrollmentDeadline) return 0

  const today = new Date()
  const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate())

  const [year, month, day] = enrollmentDeadline.split('-').map(Number)
  const deadlineLocal = new Date(year, month - 1, day)

  const diffTime = deadlineLocal.getTime() - todayLocal.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

  return Math.max(0, diffDays)
}

export const calculateYearDiff = (from: Date, to: Date = new Date()): number => {
  let years = to.getUTCFullYear() - from.getUTCFullYear()
  if (
    to.getUTCMonth() < from.getUTCMonth() ||
    (to.getUTCMonth() === from.getUTCMonth() && to.getUTCDate() < from.getUTCDate())
  ) {
    years--
  }
  return years
}

export const getDateParts = (value: string): [number, number, number] => {
  const [day, month, year] = value.split('/').map(Number)
  return [day, month, year]
}
